/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { CommentArrowLeft16Regular, CommentMultiple16Regular, Tag16Regular } from '@fluentui/react-icons';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { Separator } from '@fluentui/react/lib/Separator';
import { Stack } from '@fluentui/react/lib/Stack';
import { ITextProps, ITextSlots } from '@fluentui/react/lib/Text';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { QnaMakerQnaStore } from 'src/qnaMaker/core/stores/QnaMakerQnaStore';
import styled from 'styled-components';

type QnaMakerQnaCardProps = {
    style?: React.CSSProperties;
    checked: boolean;
    isSelected: boolean;
    setIsSelected: () => void;
    qnaData: QnaMakerQnaStore;
    isCheckboxVisible: boolean;
    toggleCheckbox: (idx: number) => void;
};

const messages = defineMessages({
    checkbox: {
        id: 'qnaMaker.qnaMakerQnaCard.checkbox',
        defaultMessage: 'Checkbox'
    },
    alternateQuestions: {
        id: 'qnaMaker.qnaMakerQnaCard.alternateQuestions',
        defaultMessage: 'Alternate questions'
    },
    followUpPrompts: {
        id: 'qnaMaker.qnaMakerQnaCard.followUpPrompts',
        defaultMessage: 'Follow up prompts'
    },
    metadata: {
        id: 'qnaMaker.qnaMakerQnaCard.metadata',
        defaultMessage: 'Metadata'
    }
});

const theme = getScopedTheme('QnaMakerQnaCard');

const Container = styled(Stack)<{ isSelected: boolean }>(props => ({
    cursor: 'pointer',
    borderBottom: theme.bottomBorder,
    backgroundColor: props.isSelected ? theme.focussingColor : 'unset',
    '&:hover': {
        backgroundColor: theme.hoveringColor
    }
}));

const sampleContainerWarpStyles = classNamesFunction<ITextProps, IComponentStyles<ITextSlots>>()({
    root: {
        width: 'calc(100% - 110px)'
    }
});

const sampleTextTitleWarpStyles = classNamesFunction<ITextProps, IComponentStyles<ITextSlots>>()({
    root: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        width: '100%'
    }
});
const sampleTextWarpStyles = classNamesFunction<ITextProps, IComponentStyles<ITextSlots>>()({
    root: {
        textAlign: 'start',
        overflowWrap: 'anywhere',
        overflow: 'hidden',
        display: '-webkit-box',
        '-webkit-line-clamp': '2',
        '-webkit-box-orient': 'vertical'
    }
});

export const QnaMakerQnaCard = observer((props: QnaMakerQnaCardProps) => {
    const { qnaData, checked, isSelected, setIsSelected, toggleCheckbox, isCheckboxVisible } = props;

    const { localize } = useLocalization();

    const [checkboxVisibility, setCheckboxVisibility] = React.useState(isCheckboxVisible ? 'visible' : 'hidden');

    React.useEffect(() => setCheckboxVisibility(isCheckboxVisible ? 'visible' : 'hidden'), [isCheckboxVisible]);

    const onMouseOver = () => {
        if (checkboxVisibility === 'hidden') {
            setCheckboxVisibility('visible');
        }
    };

    const onMouseOut = (e: React.MouseEvent<HTMLDivElement>) => {
        const parent = e.currentTarget;
        const related = e.relatedTarget;
        const inside = parent.contains(related as Node);
        if (!checked && !isCheckboxVisible && !inside) {
            setCheckboxVisibility('hidden');
        }
    };

    return (
        <Container
            horizontal
            data-is-focusable
            id={`qna${qnaData.id}`}
            isSelected={isSelected}
            onClick={setIsSelected}
            onFocus={setIsSelected}
            onMouseOut={onMouseOut}
            onMouseOver={onMouseOver}
            tokens={{ childrenGap: 5, padding: '10px' }}
            style={props.style || {}}>
            <Stack verticalAlign="center" tokens={{ padding: '0 10px 0 0' }}>
                <Checkbox
                    checked={checked}
                    ariaLabel={localize(messages.checkbox)}
                    onChange={() => toggleCheckbox(qnaData?.id)}
                    styles={{ root: { visibility: checkboxVisibility } }}
                />
            </Stack>
            <Stack grow tokens={{ childrenGap: 20 }} styles={sampleContainerWarpStyles}>
                <LuisText fontWeight="bold" styles={sampleTextTitleWarpStyles}>
                    {qnaData.questions[0]}
                </LuisText>
                <LuisText styles={sampleTextWarpStyles}>{qnaData.answer}</LuisText>
            </Stack>
            <Separator vertical />
            <Stack horizontalAlign="center" tokens={{ childrenGap: 10 }} styles={{ root: { minWidth: '50px' } }}>
                {qnaData.questions.length > 1 && (
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
                        <CommentMultiple16Regular aria-label={localize(messages.alternateQuestions)} />
                        <LuisText>{qnaData.questions.length}</LuisText>
                    </Stack>
                )}
                {qnaData.dialog.prompts.length > 0 && (
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
                        <CommentArrowLeft16Regular aria-label={localize(messages.followUpPrompts)} />
                        <LuisText>{qnaData.dialog.prompts.length}</LuisText>
                    </Stack>
                )}
                {Object.keys(props.qnaData.metadata).length > 0 && (
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
                        <Tag16Regular aria-label={localize(messages.metadata)} />
                        <LuisText>{Object.keys(qnaData.metadata).length}</LuisText>
                    </Stack>
                )}
            </Stack>
        </Container>
    );
});
